import { env } from "@/config";
import getFeaturesFlagCommerceService from "@/services/featuresFlagCommerce/getFeaturesFlagCommerceService";

const MOCKS_FLAG_CODE = "AFFILIATION_MOCKS_ENABLED";

type CommerceFeatureFlag = {
  code?: string;
  // Se guarda como número (1/0), no como booleano: los 43 flags de la tabla son numéricos.
  active?: number | boolean;
};

// El back office no tiene un store de feature flags, así que se consultan aquí. El día que lo tenga,
// este es el único archivo que cambia.
// El flag no cambia durante la sesión, así que se consulta una vez y se reutiliza la promesa.
// Sin esto, cada consulta de la bandeja costaría dos llamadas de red en vez de una.
let cachedHost: Promise<string> | null = null;

const readMocksEnabled = async (): Promise<boolean> => {
  // El servicio de flags es JavaScript sin tipos, así que la forma se declara aquí. Devuelve la
  // respuesta de axios completa, y el arreglo de flags viaja en `data`.
  const response = (await getFeaturesFlagCommerceService()) as
    | { data?: CommerceFeatureFlag[] }
    | undefined;
  const flags = response?.data;

  // Una respuesta con otra forma no es un fallo, es un "no": se resuelve igual que si el flag no
  // existiera, que es lo que ocurre en producción.
  if (!Array.isArray(flags)) {
    return false;
  }

  // Se busca por `code` y no por `name`: los nombres se pueden repetir entre la flag de la app y la
  // del portal web (`mposRequest` es el nombre de dos flags distintos).
  const mocksFlag = flags.find((flag) => flag?.code === MOCKS_FLAG_CODE);

  return Boolean(mocksFlag?.active);
};

/**
 * Resuelve contra qué host consultar: el de los mocks si el flag está encendido, el del servicio
 * definitivo si no. 
 * @returns El host, sin la ruta. Puede venir vacío si el servicio definitivo aún no está desplegado.
 */
export const getAssistedAffiliationHost = (): Promise<string> => {
  if (!cachedHost) {
    cachedHost = readMocksEnabled()
      .then((mocksEnabled) =>
        mocksEnabled ? env.MOCK_API_URL : env.ASSISTED_AFFILIATION_API_URL
      )
      .catch(() => {
        // Se memoriza la respuesta, nunca el fallo: así un error de red no deja a la pantalla
        // apuntando toda la sesión a un host que nadie confirmó, y el siguiente intento repregunta.
        cachedHost = null;
        // Mientras tanto va al servicio real, que es lo seguro: falla a la vista en lugar de servir
        // datos de mentira sin que nadie lo note.
        return env.ASSISTED_AFFILIATION_API_URL;
      });
  }

  return cachedHost;
};

// Solo para las pruebas: cada caso necesita resolver los flags de nuevo.
export const resetAssistedAffiliationHostCache = () => {
  cachedHost = null;
};
